import { OrderChannel } from 'constant/OrderChannels'
import { sortBy, uniq } from 'lodash-es'
import {
    DiscountType,
    ModifierGroupContract,
    OrderOwnerContract,
    POSVariantGroup,
} from 'models'
import { useReducer } from 'react'
import {
    MenuViewModel,
    ItemViewModel,
    OrderItemViewModel,
    ComboViewModel,
    EntityModifierViewModel,
    ModifierOptionLookup,
    ISelectItemInCart,
    OrderFulfilmentType,
    DiscountContractWithCustom,
    SelectItemInCartType,
    DiscountContractWithItemId,
    IOnlineStoreWithBrand,
    IProductPanelState,
} from 'store/models/sellModels'
import { updateLineTotalWithDiscount } from 'views/Sale/utils/orderItemUtil'
import { getOrderName } from '../useOrderDetail'
import { useApplyLoggerDispatch } from 'utilities/debug/logUtil'
import {
    ALL_ITEMS_CATEGORY_ID,
    MENUSECTION_ALL_ITEMS,
    selectDefaultMenuSection,
} from 'store/utils/normalizeDependencyData'
import Guid from 'utilities/Guid'
import OrderItemViewError from 'store/utils/OrderItemViewError'
import dayjs, { Dayjs } from 'dayjs'

const OrderChannelTable = OrderChannel.TableService

export type TOldSaleData = {
    itemsMap?: Record<string, ItemViewModel>
    modifierList?: EntityModifierViewModel[]
    modifierGroupsMap?: Record<string, ModifierGroupContract>
    modifierOptionsMap?: ModifierOptionLookup
    variantGroupsMap?: Record<string, POSVariantGroup>
    comboChoicesMap?: Record<string, ComboViewModel[]>
}

export interface ITSPOSState extends IProductPanelState {
    latestOrderItem: OrderItemViewModel | undefined
    selectedItemInCart: ISelectItemInCart | undefined
    cartOrderItems: OrderItemViewModel[]
    showCustomerSelectionDialog: boolean
    isEditingSelectedItem: boolean
    showCartDiscountDialog: boolean

    relatedOrders: OrderOwnerContract[]
    focusOrder: OrderOwnerContract | undefined

    // Old data for display loaded old order
    removedData: TOldSaleData
    fulfillmentType: OrderFulfilmentType
    excludeAutoDiscountIds: string[]
    orderChannel: OrderChannel
    selectEntireOrderDiscounts: DiscountContractWithCustom[]
    orderCustomerID?: string
    orderCustomerAddressID?: string

    // service charge dialog
    showServiceChargeDialog: boolean
    serviceChargeOnTableIds: string[]

    // selective sending to Kitchen feature
    showSendToKitchenDialog: boolean

    orderNote?: string
    addNote?: (note: string) => void

    isCartReady: boolean
    isReloadAllOrders: boolean
    noteList: INoteList[]
    //temperary disable edit line for draft line while waiting background job like send to kds
    preventEditLines: {
        orderLineID: string
        /** expiration time for disable editing */
        exp: Dayjs
    }[]
}

export interface INoteList {
    orderId: string
    note: string
}

export type TablePOSAction =
    | {
          type: 'SELECT_MENU'
          payload: {
              selectedMenu: MenuViewModel
          }
      }
    | {
          type: 'SET_RELATED_ORDERS'
          payload: OrderOwnerContract[]
      }
    | {
          type: 'SET_FOCUS_ORDER'
          payload: OrderOwnerContract | undefined
      }
    | {
          type: 'SELECT_CATEGORY'
          payload: string
      }
    | {
          type: 'ADD_SALE_ITEM'
          payload: {
              newCartOrderItems: OrderItemViewModel[]
              latestOrderItem: OrderItemViewModel | undefined
          }
      }
    | {
          type: 'UPDATE_SALE_ITEM'
          payload: {
              newOrderItems: OrderItemViewModel[]
              latestOrderItem: OrderItemViewModel
          }
      }
    | {
          type: 'REMOVE_SALE_ITEM'
          payload: ISelectItemInCart
      }
    | {
          type: 'SELECT_CART_ITEM'
          payload: ISelectItemInCart
      }
    | {
          type: 'SELECT_MENUSECTION'
          payload: string
      }
    | {
          type: 'MERGE_DISCOUNT_ITEM'
          payload: OrderItemViewModel[]
      }
    | {
          type: 'SET_CUSTOMER_SELECTION_DIALOG_STATUS'
          payload: boolean
      }
    | {
          type: 'SET_CART_DISCOUNT_DIALOG_STATUS'
          payload: boolean
      }
    | {
          type: 'SET_IS_EDITING_SELECTED_ITEM'
          payload: boolean
      }
    | {
          type: 'CLEAR_CART_ITEMS'
      }
    | {
          type: 'SET_CART_ITEMS'
          payload: OrderItemViewModel[]
      }
    | {
          type: 'ASSIGN_CUSTOMER_TO_ORDER'
          payload: {
              customerID: string
              customerAddressID?: string
          }
      }
    | {
          type: 'SET_SERVICE_CHARGE_DIALOG_STATUS'
          payload: {
              status: boolean
              selectedTableIds: string[]
          }
      }
    | {
          type: 'SET_SEND_TO_KITCHEN_DIALOG_STATUS'
          payload: boolean
      }
    | {
          type: 'REMOVE_CART_ITEMS'
          payload: string[]
      }
    | {
          type: 'SELECT_BRNAD'
          payload: string
      }
    | {
          type: 'SELECT_ONLINE_STORE'
          payload: {
              selectOnlineStore: IOnlineStoreWithBrand
              menus: MenuViewModel[]
          }
      }
    | {
          type: 'SEARCH_SALE_ITEM'
          payload: string
      }
    | {
          type: 'REMOVE_ORDER_DISCOUNT_ITEM'
          payload: {
              data: DiscountContractWithItemId
          }
      }
    | {
          type: 'CHECK_ITEM_VIEW_ERROR'
          payload: {
              errorCheck: OrderItemViewError
          }
      }
    | {
          type: 'SET_NOTE_TO_ORDER'
          payload: {
              orderId: string
              note: string
          }
      }
    | {
          type: 'ADD_PREVENT_EDIT_LINES'
          /**order line ids */
          payload: string[]
      }
    | {
          type: 'REMOVE_PREVENT_EDIT_LINES'
          /**order line ids */
          payload: string[]
      }
    | { type: 'CLEAR_PREVENT_EDIT_LINES' }
    | { type: 'TS_SET_RELOAD_ALL_ORDER'; payload: boolean }

export const DefaultSaleSelectState: ITSPOSState = {
    selectedCategoryId: ALL_ITEMS_CATEGORY_ID,
    selectedMenu: undefined,
    items: [],
    relatedOrders: [],
    focusOrder: undefined,
    latestOrderItem: undefined,
    cartOrderItems: [],
    removedData: {},
    selectedItemInCart: undefined,
    fulfillmentType: OrderFulfilmentType.DINE_IN,
    excludeAutoDiscountIds: [],
    orderChannel: OrderChannelTable,
    selectEntireOrderDiscounts: [],
    showCustomerSelectionDialog: false,
    showCartDiscountDialog: false,
    isEditingSelectedItem: false,
    orderCustomerID: '',
    orderCustomerAddressID: '',
    showServiceChargeDialog: false,
    serviceChargeOnTableIds: [],
    showSendToKitchenDialog: false,
    selectedBrandID: '',
    selectOnlineStoreId: '',
    searchString: '',
    selectedMenuSectionId: Guid.empty,
    previousSelected: {
        categoryId: ALL_ITEMS_CATEGORY_ID,
        menuSectionId: MENUSECTION_ALL_ITEMS,
    },
    isCartReady: false,
    isReloadAllOrders: false,
    noteList: [],
    preventEditLines: [],
}

const saleTableSelectReducer = (
    state: ITSPOSState,
    action: TablePOSAction,
): ITSPOSState => {
    switch (action.type) {
        case 'SELECT_MENU': {
            const { selectedMenu } = action.payload
            const menuItems = selectedMenu?.items ?? []
            return {
                ...state,
                selectedMenu,
                selectedCategoryId: ALL_ITEMS_CATEGORY_ID,
                selectedMenuSectionId: selectDefaultMenuSection(
                    selectedMenu,
                    state.selectedBrandID,
                    menuItems,
                ),
                items: menuItems,
                selectOnlineStoreId: selectedMenu?.isOnlineMenu
                    ? state.selectOnlineStoreId
                    : undefined,
            }
        }
        case 'SELECT_MENUSECTION': {
            return {
                ...state,
                selectedMenuSectionId: action.payload,
                selectedCategoryId: ALL_ITEMS_CATEGORY_ID,
            }
        }
        case 'SELECT_CATEGORY': {
            return {
                ...state,
                selectedCategoryId: action.payload,
                selectedMenuSectionId: MENUSECTION_ALL_ITEMS,
            }
        }
        case 'SELECT_BRNAD': {
            return {
                ...state,
                selectedBrandID: action.payload,
            }
        }
        case 'SELECT_ONLINE_STORE': {
            const { menus, selectOnlineStore } = action.payload
            const selectOnlineStoreMenus =
                selectOnlineStore.onlineStoreMenus ?? []
            const selectedMenu = menus.find(
                (m) => m.menuId === selectOnlineStoreMenus[0]?.menuID,
            )
            const menuItems = selectedMenu?.items ?? []
            return {
                ...state,
                selectedMenu,
                selectedMenuSectionId: selectDefaultMenuSection(
                    selectedMenu!,
                    state.selectedBrandID,
                    menuItems,
                ),
                selectedCategoryId: ALL_ITEMS_CATEGORY_ID,
                items: menuItems,
                selectOnlineStoreId: selectOnlineStore.onlineStoreID,
            }
        }
        case 'SET_RELATED_ORDERS': {
            return {
                ...state,
                relatedOrders: sortBy(action.payload, [
                    (a) => {
                        if (a.orderDateTime) {
                            return new Date(a.orderDateTime)
                        }
                    },
                    (a) => getOrderName(a),
                ]),
                isCartReady: false,
                noteList: action.payload.map((m) => {
                    return {
                        orderId: m.orderID!,
                        note: m.note ?? '',
                    }
                }),
            }
        }
        case 'SET_NOTE_TO_ORDER': {
            const {
                note,
                orderId,
            }: { note: string; orderId: string } = action.payload
            let noteListTemp = state.noteList
            let idx = noteListTemp.findIndex((f) => f.orderId === orderId)
            if (idx >= 0) {
                noteListTemp[idx].note = note
            } else {
                noteListTemp.push({ orderId: orderId, note: note })
            }
            return {
                ...state,
                noteList: noteListTemp,
            }
        }
        case 'SET_FOCUS_ORDER': {
            return {
                ...state,
                focusOrder: action.payload,
            }
        }
        case 'ADD_SALE_ITEM': {
            return {
                ...state,
                cartOrderItems: action.payload.newCartOrderItems,
                latestOrderItem: action.payload.latestOrderItem,
            }
        }
        case 'UPDATE_SALE_ITEM': {
            return {
                ...state,
                cartOrderItems: action.payload.newOrderItems,
                latestOrderItem: action.payload.latestOrderItem,
            }
        }
        case 'REMOVE_SALE_ITEM': {
            const removeData = action.payload
            const targetItem = removeData.data
            let excludeAutoDiscountIds = state.excludeAutoDiscountIds

            if (removeData.type === SelectItemInCartType.item) {
                const buyXgetYDiscounts =
                    (targetItem as OrderItemViewModel).discounts
                        ?.filter((d) => d.type === DiscountType.BuyXGetY)
                        .map((d) => d.discountID!) ?? []
                excludeAutoDiscountIds = uniq([
                    ...excludeAutoDiscountIds,
                    ...buyXgetYDiscounts,
                ])

                const removeIndex = state.cartOrderItems.findIndex(
                    (i) => i === targetItem,
                )
                if (removeIndex < 0) return state
                const nextSelectItem: ISelectItemInCart | undefined =
                    removeIndex < state.cartOrderItems.length - 1
                        ? {
                              type: SelectItemInCartType.item,
                              data: state.cartOrderItems[removeIndex + 1],
                          }
                        : undefined

                return {
                    ...state,
                    cartOrderItems: state.cartOrderItems.filter(
                        (_, idx) => idx !== removeIndex,
                    ),
                    latestOrderItem: undefined,
                    selectedItemInCart: nextSelectItem,
                    excludeAutoDiscountIds,
                }
            }

            /**
             * remove discount only
             */
            const targetDiscountItem = targetItem as DiscountContractWithItemId
            if (targetDiscountItem.isAutomaticDiscount)
                excludeAutoDiscountIds = [
                    ...excludeAutoDiscountIds,
                    targetDiscountItem.discountID!!,
                ]

            return {
                ...state,
                excludeAutoDiscountIds,
                cartOrderItems: state.cartOrderItems.map((m) => {
                    if (m.id === targetDiscountItem.id) {
                        const updatedOrderItem = {
                            ...m,
                            discounts: m.discounts?.filter(
                                (f) =>
                                    f.discountID !==
                                    targetDiscountItem.discountID,
                            ),
                        }
                        updateLineTotalWithDiscount(updatedOrderItem)
                        return updatedOrderItem
                    }
                    return m
                }),
                selectedItemInCart: undefined,
                latestOrderItem: undefined,
            }
        }
        case 'SELECT_CART_ITEM':
            return {
                ...state,
                selectedItemInCart: action.payload,
            }
        case 'MERGE_DISCOUNT_ITEM': {
            const updatedItems = action.payload
            return {
                ...state,
                cartOrderItems: state.cartOrderItems.map((curItem) => {
                    const newUpdateItem = updatedItems.find(
                        (i) => i.id === curItem.id,
                    )
                    return newUpdateItem ?? curItem
                }),
            }
        }
        case 'SET_CUSTOMER_SELECTION_DIALOG_STATUS':
            return {
                ...state,
                showCustomerSelectionDialog: action.payload,
            }
        case 'SET_CART_DISCOUNT_DIALOG_STATUS':
            return {
                ...state,
                showCartDiscountDialog: action.payload,
            }

        case 'SET_IS_EDITING_SELECTED_ITEM':
            return {
                ...state,
                isEditingSelectedItem: action.payload,
            }
        case 'SET_CART_ITEMS': {
            return {
                ...state,
                cartOrderItems: action.payload,
                selectedItemInCart: undefined,
                latestOrderItem: undefined,
                isCartReady: true,
            }
        }
        case 'CLEAR_CART_ITEMS':
            return {
                ...state,
                cartOrderItems: [],
                latestOrderItem: undefined,
                selectedItemInCart: undefined,
                isCartReady: false,
            }
        case 'ASSIGN_CUSTOMER_TO_ORDER': {
            const { customerID, customerAddressID } = action.payload

            return {
                ...state,
                orderCustomerID: customerID,
                orderCustomerAddressID: customerAddressID,
            }
        }
        case 'SET_SERVICE_CHARGE_DIALOG_STATUS':
            return {
                ...state,
                showServiceChargeDialog: action.payload.status,
                serviceChargeOnTableIds: action.payload.selectedTableIds,
            }
        case 'SET_SEND_TO_KITCHEN_DIALOG_STATUS':
            return {
                ...state,
                showSendToKitchenDialog: action.payload,
            }
        case 'REMOVE_CART_ITEMS': {
            return {
                ...state,
                cartOrderItems: state.cartOrderItems.filter(
                    (i) => !action.payload.includes(i.id),
                ),
            }
        }
        case 'SEARCH_SALE_ITEM': {
            const searchString = action.payload as string

            return {
                ...state,
                searchString,
                previousSelected: {
                    categoryId: state.selectedCategoryId,
                    menuSectionId: state.selectedMenuSectionId,
                },
            }
        }
        case 'REMOVE_ORDER_DISCOUNT_ITEM': {
            let { data } = action.payload
            const targetDiscountItem = data as DiscountContractWithItemId
            const updateCartOrderItems = state.cartOrderItems.map((i) => {
                if (i.id === targetDiscountItem.id)
                    i.discounts = i.discounts?.filter(
                        (d) => d.discountID !== targetDiscountItem.discountID,
                    )
                return i
            })
            return {
                ...state,
                cartOrderItems: updateCartOrderItems,
            }
        }
        case 'CHECK_ITEM_VIEW_ERROR': {
            const errorChecker = action.payload.errorCheck
            const cartOrderItems = state.cartOrderItems.map((oi) => {
                errorChecker.initial()
                return errorChecker.validateOrderItemsView(oi)
            })
            return {
                ...state,
                cartOrderItems,
            }
        }
        case 'ADD_PREVENT_EDIT_LINES': {
            const addLines = action.payload
            let newPreventLines = state.preventEditLines
            //remove same old line id
            newPreventLines = newPreventLines.filter(
                (a) => !addLines.includes(a.orderLineID),
            )
            newPreventLines = [
                ...newPreventLines,
                ...addLines.map((a) => ({
                    orderLineID: a,
                    exp: dayjs().add(30, 'seconds'),
                })),
            ]
            return {
                ...state,
                preventEditLines: newPreventLines,
            }
        }
        case 'REMOVE_PREVENT_EDIT_LINES': {
            const addLines = action.payload
            let newPreventLines = state.preventEditLines
            newPreventLines = newPreventLines.filter(
                (a) => !addLines.includes(a.orderLineID),
            )
            return {
                ...state,
                preventEditLines: newPreventLines,
            }
        }
        case 'CLEAR_PREVENT_EDIT_LINES': {
            return { ...state, preventEditLines: [] }
        }
        case 'TS_SET_RELOAD_ALL_ORDER': {
            return { ...state, isReloadAllOrders: action.payload }
        }
        default:
            return state
    }
}

const useTableSaleSelectReducer = () => {
    const [saleSelectObject, dispatch] = useReducer(
        saleTableSelectReducer,
        DefaultSaleSelectState,
    )

    const dispatchFunc = useApplyLoggerDispatch(
        'SaleSelectAction',
        dispatch,
        saleSelectObject,
    )

    return [saleSelectObject, dispatchFunc] as const
}

export default useTableSaleSelectReducer
